import { Link } from 'react-router-dom';
import { ReactNode } from 'react';
import { useBrandingStore } from '../store/branding';
import { useUiStore } from '../store/ui';
import { useThemeSync } from '../components/layout';

function LegalShell({ title, children }: { title: string; children: ReactNode }) {
  useThemeSync();
  const branding = useBrandingStore();
  const ui = useUiStore();
  const name = branding.platformName?.trim() || 'RTMPHUB';
  return (
    <div style={{ minHeight: '100vh', background: 'var(--background)', color: 'var(--text)' }}>
      <header className="landing-nav">
        <div className="landing-container landing-nav-inner">
          <Link to="/" className="landing-brand">
            {branding.logoUrl ? <img src={branding.logoUrl} alt="" className="landing-logo" /> : <span className="landing-logo-mark"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9" /><path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5" /><circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" /><path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5" /><path d="M19.1 4.9C23 8.8 23 15.1 19.1 19.1" /></svg></span>}
            <span className="landing-brand-name">{name}</span>
          </Link>
          <div className="landing-nav-actions">
            <button className="icon-button" aria-label="Toggle theme" onClick={() => ui.toggleTheme()}>
              {ui.theme === 'dark' ? '☀️' : '🌙'}
            </button>
            <Link to="/login" className="btn primary sm">Login</Link>
          </div>
        </div>
      </header>
      <main className="landing-container" style={{ padding: '2.5rem 1.25rem 4rem', maxWidth: 860 }}>
        <h1 style={{ marginBottom: '1rem' }}>{title}</h1>
        <div className="legal-body" style={{ color: 'var(--text)', lineHeight: 1.65 }}>
          {children}
        </div>
        <p style={{ marginTop: '2rem' }}><Link to="/" className="btn secondary sm">Back to home</Link></p>
      </main>
    </div>
  );
}

export function TermsPage() {
  return (
    <LegalShell title="Terms & Conditions">
      <p><em>Effective terms governing use of RTMPHUB, operated by Jak techdrivesolution.</em></p>
      <h3>1. Introduction</h3>
      <p>These Terms &amp; Conditions govern the use of RTMPHUB, operated by Jak techdrivesolution. By registering for an account, purchasing a subscription or using our services, you agree to these terms.</p>
      <h3>2. Services offered</h3>
      <p>RTMPHUB provides online streaming infrastructure and related features according to the subscription plan purchased by the customer. Available features and limits are displayed before purchase.</p>
      <h3>3. Accounts and security</h3>
      <p>Customers must provide accurate registration information and keep account credentials confidential. Customers are responsible for activity conducted through their accounts, subject to applicable law.</p>
      <h3>4. Subscription plans and payments</h3>
      <p>Subscriptions are offered for the durations and prices displayed on the pricing page. All prices are displayed in Indian Rupees (INR). Applicable taxes, if any, are disclosed at checkout. Payment must be successfully verified before the corresponding paid service is activated.</p>
      <h3>5. Subscription validity and renewal</h3>
      <p>Each subscription is valid for the duration stated at purchase. Unless expressly offered as an automatic renewal, subscriptions expire at the end of their stated period and must be renewed manually. Customers should check their account dashboard for subscription status and expiry date.</p>
      <h3>6. Acceptable use</h3>
      <p>Customers must not use RTMPHUB to distribute unlawful content, infringe intellectual property rights, conduct fraud, transmit malicious software or interfere with the security and operation of the platform. Customers must have the necessary rights and permissions for the content they stream or republish.</p>
      <h3>7. Service availability</h3>
      <p>We aim to maintain reliable service, but uninterrupted availability cannot be guaranteed. Maintenance, network failures, third-party outages and other events may affect service availability.</p>
      <h3>8. Suspension and termination</h3>
      <p>We may restrict or suspend access where reasonably necessary to address security threats, unlawful activity, payment fraud, material violations of these terms or legal requirements. Where appropriate and lawful, we will notify affected customers.</p>
      <h3>9. Refunds and cancellations</h3>
      <p>Refunds and cancellations are governed by our Refund &amp; Cancellation Policy, available on the website.</p>
      <h3>10. Privacy</h3>
      <p>Personal information is handled according to our Privacy Policy.</p>
      <h3>11. Changes to these terms</h3>
      <p>We may update these terms when necessary. The revised version will be published on our website with its effective date.</p>
    </LegalShell>
  );
}

export function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy">
      <p><em>Effective Date: [Enter publication date]</em></p>
      <h3>1. Introduction</h3>
      <p>This Privacy Policy explains how RTMPHUB, operated by Jak techdrivesolution, collects, uses and protects information when customers access our website or use our streaming services.</p>
      <h3>2. Information we collect</h3>
      <p>Depending on how you use our services, we may collect:</p>
      <ul>
        <li>Name, email address and contact details.</li>
        <li>Account credentials and account identifiers.</li>
        <li>Subscription plan, payment status and transaction references.</li>
        <li>Technical information such as IP address, browser type, device information and service logs.</li>
        <li>Streaming configuration and usage information needed to provide the service.</li>
        <li>Information provided when contacting customer support.</li>
      </ul>
      <p>We do not intend to collect payment card credentials or UPI PINs directly through our own application.</p>
      <h3>3. How we use information</h3>
      <p>We use information to:</p>
      <ul>
        <li>Create and manage customer accounts.</li>
        <li>Activate and administer subscriptions.</li>
        <li>Verify payments and maintain transaction records.</li>
        <li>Provide streaming services and technical support.</li>
        <li>Monitor service performance and prevent fraud or abuse.</li>
        <li>Meet applicable legal and accounting obligations.</li>
      </ul>
      <h3>4. Payment processing</h3>
      <p>Payments may be processed through third-party payment providers. Those providers may process transaction information under their own applicable privacy and security policies. RTMPHUB uses the payment status and transaction references needed to manage orders and subscriptions.</p>
      <h3>5. Sharing of information</h3>
      <p>We may share relevant information with service providers, hosting providers, payment processors and professional advisers where necessary to operate our business. We may also disclose information when required by law or to protect our legal rights. We do not sell personal information as a standalone product.</p>
      <h3>6. Data security</h3>
      <p>We use reasonable technical and organisational safeguards to protect information. However, no internet transmission or storage system can be guaranteed to be completely secure.</p>
      <h3>7. Data retention</h3>
      <p>We retain information for as long as reasonably necessary to provide our services, maintain business records, resolve disputes and comply with applicable legal obligations.</p>
      <h3>8. Your rights and requests</h3>
      <p>You may contact us to request access to, correction of or deletion of personal information, subject to applicable law and legitimate record-retention requirements.</p>
      <h3>9. Changes to this policy</h3>
      <p>Updates will be published on this page with a revised effective date.</p>
    </LegalShell>
  );
}

export function RefundPage() {
  return (
    <LegalShell title="Refund & Cancellation Policy">
      <p><em>Effective Date: [Enter publication date]</em></p>
      <h3>1. Digital subscription service</h3>
      <p>RTMPHUB provides digital streaming services. No physical product is shipped. After a payment has been verified, the purchased subscription is activated according to the selected plan.</p>
      <h3>2. Cancellation</h3>
      <p>Customers may request cancellation of a subscription by contacting our support team. Cancellation prevents future renewal where applicable. For manually renewed plans, customers can simply choose not to purchase another term. Cancellation does not automatically entitle a customer to a refund for an already activated subscription.</p>
      <h3>3. Refund eligibility</h3>
      <p>A refund may be considered in circumstances such as:</p>
      <ul>
        <li>A verified duplicate payment for the same order.</li>
        <li>A successful payment where the subscription was not activated and the issue cannot reasonably be resolved.</li>
        <li>An eligible billing error.</li>
        <li>Other circumstances where a refund is required by applicable law or expressly approved under our policy.</li>
      </ul>
      <p>Unused subscription time, a change of mind or failure to use the service does not automatically qualify for a refund. Any applicable consumer rights remain unaffected.</p>
      <h3>4. Incorrect or failed payments</h3>
      <p>If an amount is debited but the order remains unpaid or the service is not activated, customers should contact support with their order ID and transaction reference. We will investigate the transaction and, where appropriate, arrange activation or initiate a refund.</p>
      <h3>5. How to request a refund</h3>
      <p>Please email [Enter support email] with:</p>
      <ul>
        <li>Registered account email.</li>
        <li>Order or transaction ID.</li>
        <li>Payment date and amount.</li>
        <li>Description of the issue.</li>
      </ul>
      <p>Do not send your UPI PIN, password, OTP or full card details.</p>
      <h3>6. Refund processing time</h3>
      <p>We aim to review refund requests within 5–7 business days. Where a refund is approved, it will be initiated to the original payment method where supported. The time taken for the refunded amount to appear in the customer's account depends on the payment provider and bank.</p>
    </LegalShell>
  );
}
